import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { FolderSimplePlus, Plus, CheckCircle } from "@phosphor-icons/react";

export default function AddToCollectionMenu({ postId, trigger }) {
    const [open, setOpen] = useState(false);
    const [cols, setCols] = useState([]);
    const [busy, setBusy] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [isPublic, setIsPublic] = useState(true);
    const [creating, setCreating] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/collections", { params: { mine: true } });
            setCols(data);
        } catch (err) {
            // non-fatal: dropdown opens with empty list; user can still create a new one
            console.error("load collections failed:", err);
        }
    };

    useEffect(() => {
        if (open) load();
    }, [open]);

    const addTo = async (cid) => {
        if (busy) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/collections/${cid}/posts/${postId}`);
            if (data.already) toast.message("Already in this trip plan");
            else toast.success("Added to trip plan");
            setOpen(false);
        } catch (_) {
            toast.error("Could not add");
        } finally {
            setBusy(false);
        }
    };

    const submitCreate = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            const { data: col } = await api.post("/collections", {
                title, description, is_public: isPublic,
            });
            await api.post(`/collections/${col.id}/posts/${postId}`);
            toast.success(`Created “${col.title}” and added this post`);
            setTitle(""); setDescription(""); setIsPublic(true);
            setCreateOpen(false);
            setOpen(false);
        } catch (_) {
            toast.error("Could not create collection");
        } finally {
            setCreating(false);
        }
    };

    return (
        <>
            <DropdownMenu open={open} onOpenChange={setOpen}>
                <DropdownMenuTrigger asChild>
                    {trigger || (
                        <button
                            data-testid={`add-to-collection-trigger-${postId}`}
                            className="text-stone-400 hover:text-[#990000] inline-flex items-center gap-1 transition-colors"
                            aria-label="Add to trip plan"
                        >
                            <FolderSimplePlus size={16} weight="regular" />
                        </button>
                    )}
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                    <DropdownMenuLabel className="text-xs uppercase tracking-[0.15em] text-stone-500">
                        Add to a trip plan
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {cols.length === 0 ? (
                        <div className="px-3 py-3 text-xs text-stone-500">No collections yet — create one below.</div>
                    ) : (
                        <div className="max-h-56 overflow-y-auto">
                            {cols.map((c) => (
                                <DropdownMenuItem
                                    key={c.id}
                                    data-testid={`add-to-collection-item-${c.id}`}
                                    onClick={() => addTo(c.id)}
                                    className="flex items-center justify-between"
                                >
                                    <span className="truncate">{c.title}</span>
                                    <span className="text-xs text-stone-400 ml-2">{c.post_count}</span>
                                </DropdownMenuItem>
                            ))}
                        </div>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        data-testid="add-to-collection-create-trigger"
                        onClick={(e) => { e.preventDefault(); setOpen(false); setCreateOpen(true); }}
                        className="text-[#990000] font-medium"
                    >
                        <Plus size={14} className="mr-2" />
                        New trip collection…
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="sm:max-w-[480px]" data-testid="create-collection-dialog">
                    <DialogHeader>
                        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#990000] mb-1">New Trip Plan</div>
                        <DialogTitle className="font-heading text-2xl font-semibold text-[#0A0A0A]">Create a trip collection</DialogTitle>
                        <DialogDescription className="text-stone-500">
                            Bundle related flights, hotels, destinations & activities into one shareable trip plan.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submitCreate} className="space-y-4 pt-2">
                        <div>
                            <Label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">Title</Label>
                            <Input data-testid="create-collection-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
                                   placeholder="e.g., Kerala 7-day backpack" maxLength={120}
                                   className="mt-2 h-11 border-stone-300 focus-visible:ring-[#990000] focus-visible:border-[#990000]" required />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">Description</Label>
                            <Textarea data-testid="create-collection-desc-input" value={description} onChange={(e) => setDescription(e.target.value)}
                                      placeholder="What's the trip about? Who's it for?" rows={3}
                                      className="mt-2 border-stone-300 focus-visible:ring-[#990000] focus-visible:border-[#990000]" />
                        </div>
                        <div className="flex items-center justify-between bg-stone-50 border border-stone-200 rounded-lg p-3">
                            <div>
                                <div className="text-sm font-medium text-[#0A0A0A]">Public</div>
                                <div className="text-xs text-stone-500">{isPublic ? "Anyone signed in can see this trip plan" : "Only you can see this"}</div>
                            </div>
                            <Switch
                                data-testid="create-collection-public-toggle"
                                checked={isPublic}
                                onCheckedChange={setIsPublic}
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
                            <Button type="submit" disabled={creating} data-testid="create-collection-submit-btn"
                                    className="bg-[#990000] hover:bg-[#7A0000] text-white">
                                <CheckCircle size={14} weight="bold" className="mr-1.5" />
                                {creating ? "Creating…" : "Create & add post"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}
